import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNotifications } from './useNotifications';
import { useQueryClient } from '@tanstack/react-query';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export function useWebSocket() {
  const { user } = useAuth();
  const { showNotificationPopup } = useNotifications();
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempt = useRef(0);
  const unmounted = useRef(false);

  const connect = useCallback(() => {
    if (unmounted.current) return;
    if (!(user as any)?.id) return;

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent reconnect loop
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host || 'localhost:5000';
    const wsUrl = `${protocol}//${host}/ws`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttempt.current = 0;
      const token = localStorage.getItem('auth_token');
      ws.send(JSON.stringify({
        type: 'auth',
        token: token,
        userId: (user as any).id
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'notification': {
            const notifType = data.notification_type || data.data?.type;
            const message = data.data?.message || data.message || 'Hai una nuova notifica';
            showNotificationPopup({
              id: Date.now(),
              userId: (user as any).id,
              type: notifType,
              message,
              data: data.data,
              isRead: false,
              fromUserId: data.data?.fromUserId,
              createdAt: new Date().toISOString()
            });
            break;
          }
          case 'new_message': {
            const senderName = data.sender?.firstName || 'Qualcuno';

            // Segnale per il soft-ask delle notifiche push: e' il momento in cui
            // il beneficio e' ovvio ("ti avvisiamo quando ti risponde"). Evento
            // custom per non accoppiare il WebSocket al componente del prompt.
            window.dispatchEvent(new CustomEvent('cercamanti:message-received'));
            showNotificationPopup({
              id: Date.now(),
              userId: (user as any).id,
              type: 'new_message',
              message: `${senderName} ti ha scritto. È online adesso.`,
              data: { senderId: data.sender?.id },
              isRead: false,
              fromUserId: data.sender?.id,
              createdAt: new Date().toISOString()
            });

            if (data.message?.conversationId) {
              queryClient.invalidateQueries({
                queryKey: ['/api/conversations', data.message.conversationId, 'messages']
              });
            }
            queryClient.invalidateQueries({
              queryKey: ['/api/messages/unread']
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/conversations']
            });
            break;
          }

          case 'message_deleted': {
            // Remove blocked image from cache
            if (data.conversationId) {
              queryClient.setQueryData(
                ['/api/conversations', data.conversationId, 'messages'],
                (old: any) => old ? old.filter((m: any) => m.id !== data.messageId) : old
              );
            }
            break;
          }

          case 'typing': {
            const conversationId = data.conversation_id || data.conversationId;
            if (conversationId) {
              window.dispatchEvent(
                new CustomEvent('chat-typing', {
                  detail: {
                    conversationId,
                    userId: data.user_id || data.userId,
                    username: data.username,
                  },
                })
              );
            }
            break;
          }

          case 'stop_typing': {
            const conversationId = data.conversation_id || data.conversationId;
            if (conversationId) {
              window.dispatchEvent(
                new CustomEvent('chat-stop-typing', {
                  detail: {
                    conversationId,
                    userId: data.user_id || data.userId,
                  },
                })
              );
            }
            break;
          }

          case 'bio_like': {
            const likerName = data.liker?.firstName || 'Qualcuno';
            showNotificationPopup({
              id: Date.now(),
              userId: (user as any).id,
              type: 'bio_like',
              message: `${likerName} apprezza la tua bio`,
              data: { likerId: data.liker?.id },
              isRead: false,
              fromUserId: data.liker?.id,
              createdAt: new Date().toISOString()
            });
            break;
          }

          case 'interest_like': {
            const interestLikerName = data.liker?.firstName || 'Qualcuno';
            showNotificationPopup({
              id: Date.now(),
              userId: (user as any).id,
              type: 'interest_like',
              message: `${interestLikerName} condivide i tuoi interessi`,
              data: { likerId: data.liker?.id, interest: data.interest },
              isRead: false,
              fromUserId: data.liker?.id,
              createdAt: new Date().toISOString()
            });
            break;
          }

          case 'photo_like': {
            const photoLikerName = data.liker?.firstName || 'Qualcuno';
            showNotificationPopup({
              id: Date.now(),
              userId: (user as any).id,
              type: 'photo_like',
              message: `${photoLikerName} apprezza la tua foto`,
              data: { likerId: data.liker?.id, photoId: data.photoId },
              isRead: false,
              fromUserId: data.liker?.id,
              createdAt: new Date().toISOString()
            });
            break;
          }

          case 'online_status_update':
            window.dispatchEvent(
              new CustomEvent('online-status-update', {
                detail: data.statuses,
              })
            );
            break;

          default:
            console.log('Unknown WebSocket message type:', data.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      wsRef.current = null;
      if (!unmounted.current) {
        const delay = Math.min(
          RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt.current),
          RECONNECT_MAX_MS
        );
        reconnectAttempt.current++;
        console.log(`WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttempt.current})`);
        reconnectTimer.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }, [user, showNotificationPopup, queryClient]);

  useEffect(() => {
    unmounted.current = false;
    connect();

    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on cleanup
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN
  };
}
